import { memo } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { CommonDialog } from "@/components/common/CommonDialog";
import {
  MOBILE_SHEET_FOOTER_BUTTON_GAP,
  mobileSheetCancelButtonSx,
  mobileSheetConfirmButtonSx,
} from "@/components/common/mobileSheetFooterButtons";
import type { RejectTarget } from "./DocumentTile";

interface RejectDocumentDialogProps {
  target: RejectTarget;
  rejectReason: string;
  busyDocId: string | null;
  showWhatsApp: boolean;
  onReasonChange: (value: string) => void;
  onConfirm: () => void;
  onConfirmAndWhatsApp: () => void;
  onClose: () => void;
}

export const RejectDocumentDialog = memo(function RejectDocumentDialog({
  target,
  rejectReason,
  busyDocId,
  showWhatsApp,
  onReasonChange,
  onConfirm,
  onConfirmAndWhatsApp,
  onClose,
}: RejectDocumentDialogProps) {
  return (
    <CommonDialog
      isOpen
      handleClose={() => {
        if (!busyDocId) onClose();
      }}
      maxWidth="sm"
      sheetSize="medium"
      sheetTitle="Reprovar documento"
      footer={
        <Stack spacing={2} sx={{ width: "100%", pt: 0.5 }}>
          <Stack spacing={MOBILE_SHEET_FOOTER_BUTTON_GAP}>
            <Button
              fullWidth
              variant="contained"
              color="error"
              disableElevation
              disabled={!rejectReason.trim() || Boolean(busyDocId)}
              onClick={onConfirm}
              sx={mobileSheetConfirmButtonSx}
            >
              {busyDocId ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "Confirmar reprovação"
              )}
            </Button>
            {showWhatsApp ? (
              <Button
                fullWidth
                variant="outlined"
                color="success"
                disabled={!rejectReason.trim() || Boolean(busyDocId)}
                onClick={onConfirmAndWhatsApp}
                startIcon={
                  busyDocId ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <WhatsAppIcon />
                  )
                }
                sx={{
                  ...mobileSheetCancelButtonSx,
                  borderWidth: 1.5,
                  py: 1.1,
                }}
              >
                Confirmar e enviar WhatsApp
              </Button>
            ) : null}
          </Stack>
          <Divider />
          <Button
            fullWidth
            variant="text"
            color="inherit"
            disabled={Boolean(busyDocId)}
            onClick={onClose}
            sx={{
              textTransform: "none",
              color: "text.secondary",
              py: 0.75,
            }}
          >
            Cancelar
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2.5} sx={{ py: { xs: 0.5, sm: 1 }, px: { xs: 0, sm: 0.5 } }}>
        <Box
          sx={{
            p: 1.75,
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
            bgcolor: "grey.50",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5 }}
          >
            Documento selecionado
          </Typography>
          <Typography variant="subtitle2" fontWeight={700}>
            {target.categoryLabel}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {target.title}
          </Typography>
        </Box>

        <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
          O paciente verá o motivo informado abaixo na área de documentos da
          loja.
        </Alert>

        <TextField
          label="Motivo da reprovação"
          placeholder="Ex.: documento ilegível, data vencida, CPF divergente…"
          value={rejectReason}
          onChange={(event) => onReasonChange(event.target.value)}
          multiline
          minRows={4}
          fullWidth
          autoFocus
          helperText="Descreva de forma clara o que o paciente precisa corrigir ou reenviar."
        />
      </Stack>
    </CommonDialog>
  );
});
